import omitBy from 'lodash/omitBy';
import isUndefined from 'lodash/isUndefined';

import {isPersistedCustomProperty} from 'common/widgets/custom-property-modal/util';
import {reporter} from 'common/report/error-reporting';
import {MetricType} from 'common/query-builder/query-block/constants';

import {
  BehaviorMetric,
  BehaviorShowClause as BehaviorShowClauseType,
  FormulaShowClause as FormulaShowClauseType,
  GroupClause as GroupClauseType,
  InlineFormula,
  InsightsBookmarkParams,
  InsightsResourceType,
  ShowClause as ShowClauseType,
} from '../util/types';

export function sanitizeIfCustomProperty(property: BehaviorMetric['measurement']['property']) {
  if (!property.customPropertyId) {
    return property;
  }

  const {customProperty, ...rest} = property;
  return {
    ...rest,
    // customProperty (custom property definition) should not be passed as an attr for persisted
    // custom properties. However, old bookmark did store customProperty for persisted custom
    // properties, which we remove below.
    ...(!isPersistedCustomProperty(property.customPropertyId) && customProperty ? {customProperty} : {}),
  };
}

export function isBehaviorShowClause(showClause: ShowClauseType): showClause is BehaviorShowClauseType {
  if (`behavior` in showClause && `measurement` in showClause) {
    return true;
  }

  if (`behavior` in showClause || `measurement` in showClause) {
    reporter.error(`Non-behavior show clause has unexpected "behavior"/"measurement" key`, {showClause});
  }
  return false;
}

export function getBehaviorShowClauses(showClauses: ShowClauseType[]): BehaviorShowClauseType[] {
  if (Array.isArray(showClauses) === false) {
    return [];
  }

  return showClauses.filter(isBehaviorShowClause);
}

export function isFormulaShowClause(showClause: ShowClauseType): showClause is FormulaShowClauseType {
  if (`formula` in showClause) {
    if (Object.keys(showClause).length !== 1) {
      reporter.error(`Formula show clause has unexpected keys besides "formula"`, {showClause});
    }
    return true;
  }
  return false;
}

export function getFormulaShowClauses(showClauses: ShowClauseType[]): FormulaShowClauseType[] {
  return showClauses.filter(isFormulaShowClause);
}

export function isValidFormula(formula: InlineFormula): boolean {
  return Boolean(formula && formula.name && formula.definition);
}

export function transformBookmarkToMultiMetricBookmark(bookmarkParams: InsightsBookmarkParams): InsightsBookmarkParams {
  // defensive programming, just in case the params pass in is falsy
  if (!bookmarkParams) return bookmarkParams;
  return {
    ...bookmarkParams,
    sections: {
      ...bookmarkParams.sections,
      cohorts: [],
      group: moveCohortToGroupSection(bookmarkParams),
      show: bookmarkParams.sections.show.map((show) => {
        if (isFormulaShowClause(show)) {
          return {
            formula: {...show.formula},
          };
        } else {
          const {behavior, measurement} = transformShowToBehaviorMetric(show);
          return {
            behavior: {
              type: MetricType.Event,
              resourceType: behavior.resourceType,
              ...omitBy(behavior, isUndefined),
            },
            measurement: {
              math: measurement.math,
              ...omitBy(measurement, isUndefined),
            },
          };
        }
      }),
    },
  };
}

export function moveCohortToGroupSection(bookmarkParams: InsightsBookmarkParams) {
  const cohortsSection = bookmarkParams.sections.cohorts || [];
  // Place cohorts at the top level in group section
  const cohorts = [];
  cohortsSection.forEach((cohort) => {
    cohorts.push(cohort);
  });

  const groupSection = bookmarkParams.sections.group || [];
  if (cohorts.length > 0) {
    const value = cohorts.map((cohort) => cohort.name);
    const groupByCohorts: GroupClauseType = {
      value,
      cohorts,
      resourceType: InsightsResourceType.Events,
    };
    return [groupByCohorts, ...groupSection];
  }

  return groupSection;
}

export function transformShowToBehaviorMetric(show: BehaviorShowClauseType): BehaviorMetric {
  if (show.behavior && show.measurement) {
    return {
      behavior: show.behavior,
      measurement: show.measurement,
    };
  }
  const {
    math,
    perUserAggregation,
    property,
    rolling,
    cumulative,
    multiAttribution,
    search,
    resourceType,
    profileType,
    value,
    filter,
    filterDeterminer,
    dataGroupId,
    dataset,
    datasetId,
    projectId,
    customBucket,
  } = show;

  return {
    measurement: {
      math,
      property,
      cumulative,
      perUserAggregation,
      rolling,
      multiAttribution,
    },
    behavior: {
      type: MetricType.Event,
      search,
      resourceType,
      profileType,
      value,
      filter,
      filterDeterminer,
      dataGroupId,
      dataset,
      datasetId,
      projectId,
      customBucket,
    },
  };
}
